import React, { useCallback, useState } from 'react';

export type AttachedMenu = {
  open: boolean;
  x: [number, number];
  y: [number, number];
  handleClick: () => void;
  handleMousedown: (event: React.MouseEvent) => void;
  handleClose: () => void;
};

/**
 * A port of Carbon's internal useAttachedMenu (@carbon/react 1.35.0,
 * lib/internal/useAttachedMenu.js), which is not part of the public export surface.
 * Copied rather than deep-imported so a Carbon upgrade cannot move the file out from
 * under us. MenuButton uses the original, so this is the positioning contract Carbon's
 * own components rely on: x and y are [start, end] ranges, which is what lets Menu flip
 * itself when it would overflow the viewport.
 */
export function useAttachedMenu(anchor: React.RefObject<HTMLElement>): AttachedMenu {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<[[number, number], [number, number]]>([
    [-1, -1],
    [-1, -1],
  ]);

  const handleClick = useCallback(() => {
    if (open) {
      setOpen(false);
      return;
    }
    const element = anchor.current;
    if (element) {
      const { left, top, right, bottom } = element.getBoundingClientRect();
      setPosition([
        [left, right],
        [top, bottom],
      ]);
    }
    setOpen(true);
  }, [anchor, open]);

  // preventDefault stops mousedown on the trigger from blurring the menu: the blur would
  // close it and the click that follows would immediately reopen it, which reads as a
  // menu that will not stay shut.
  const handleMousedown = useCallback((event: React.MouseEvent) => event.preventDefault(), []);

  const handleClose = useCallback(() => setOpen(false), []);

  return { open, x: position[0], y: position[1], handleClick, handleMousedown, handleClose };
}
